import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

const CSAT_POPUP_DELAY = 5000; // 5 seconds after page load
const STORAGE_KEY_PREFIX = 'csat_shown_';

export const useCSATPopupTrigger = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  const { data: settings } = useQuery({
    queryKey: ['csat-settings'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('csat_settings')
        .select('*')
        .eq('is_enabled', true);

      if (error) throw error;
      return data;
    },
  });

  useEffect(() => {
    const currentPath = location.pathname;
    const storageKey = `${STORAGE_KEY_PREFIX}${currentPath}`;
    
    // Normalizar path para matching (tratar rotas dinâmicas)
    const normalizedPath = (currentPath.startsWith('/coleta-sob-demanda/') || currentPath.startsWith('/coletasobdemanda/')) && currentPath !== '/coleta-sob-demanda' && currentPath !== '/coletasobdemanda'
      ? '/coletasobdemanda/:id'
      : currentPath.startsWith('/perguntasfrequentes/')
        ? '/perguntasfrequentes'
        : currentPath;
    
    // Check if this page has CSAT enabled
    const pageSetting = settings?.find(s => s.page_path === normalizedPath);
    
    if (!pageSetting) {
      setIsOpen(false);
      return;
    }

    // Check if user has already seen the popup for this page
    const lastShown = localStorage.getItem(storageKey);
    const now = new Date();
    
    if (lastShown) {
      const lastShownDate = new Date(lastShown);
      const daysDiff = Math.floor((now.getTime() - lastShownDate.getTime()) / (1000 * 60 * 60 * 24));
      
      if (daysDiff < pageSetting.display_days) {
        setIsOpen(false);
        return;
      }
    }

    // Show popup after delay
    const timer = setTimeout(() => {
      setIsOpen(true);
      localStorage.setItem(storageKey, now.toISOString());
    }, CSAT_POPUP_DELAY);

    return () => clearTimeout(timer);
  }, [location.pathname, settings]);

  const handleClose = () => {
    setIsOpen(false);
  };

  return { isOpen, handleClose };
};
